/**
 * Reusable navigation helpers for stock-detail links.
 *
 * <CompanyLink companyCode="COMB" name="Commercial Bank of Ceylon PLC" />
 *   -> renders the company name as a subtle text link to /stocks/COMB
 *      with a "from" pointer for the back button.
 *
 * <SymbolLink companyCode="COMB" symbol="COMB.X0000" />
 *   -> renders the ticker as a monospace teal link.
 *
 * Both forward an optional `fromLabel` prop (e.g. "Portfolio") which the
 * Stock Detail page picks up via location.state to render
 * "← Back to Portfolio".
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";

const PAGE_LABEL_BY_PATH = [
  { match: /^\/dashboard/, label: "Dashboard" },
  { match: /^\/stocks$/, label: "Screener" },
  { match: /^\/best-value/, label: "Best Value Finder" },
  { match: /^\/dividends/, label: "Dividends" },
  { match: /^\/ai-insights/, label: "AI Insights" },
  { match: /^\/portfolio/, label: "Portfolio" },
  { match: /^\/reports/, label: "Reports" },
  { match: /^\/system-status/, label: "System Status" },
  { match: /^\/alerts/, label: "Alerts" },
  { match: /^\/watchlist/, label: "Watchlist" },
];

/** Resolve a friendly label for a pathname (e.g. /portfolio -> "Portfolio"). */
export const labelForPath = (pathname) => {
  if (!pathname) return null;
  const hit = PAGE_LABEL_BY_PATH.find((p) => p.match.test(pathname));
  return hit?.label || null;
};

/** Hook: build a "from" object to attach to <Link state>. */
export const useBackOrigin = (overrideLabel) => {
  const location = useLocation();
  const label = overrideLabel || labelForPath(location.pathname);
  return { from: { pathname: location.pathname, label } };
};

const baseAnchorClasses =
  "cursor-pointer transition-colors hover:text-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/40 rounded-sm";

export const CompanyLink = ({
  companyCode,
  name,
  fromLabel,
  className = "",
  testId,
  truncate = false,
  children,
}) => {
  const state = useBackOrigin(fromLabel);
  if (!companyCode) {
    return <span className={className}>{children || name}</span>;
  }
  return (
    <Link
      to={`/stocks/${companyCode}`}
      state={state}
      className={`${baseAnchorClasses} hover:underline underline-offset-2 ${
        truncate ? "truncate" : ""
      } ${className}`}
      data-testid={testId || `company-link-${companyCode}`}
    >
      {children || name}
    </Link>
  );
};

export const SymbolLink = ({
  companyCode,
  symbol,
  fromLabel,
  className = "",
  testId,
}) => {
  const state = useBackOrigin(fromLabel);
  if (!companyCode || !symbol) {
    return (
      <span className={`font-mono text-xs ${className}`}>{symbol || "—"}</span>
    );
  }
  return (
    <Link
      to={`/stocks/${companyCode}`}
      state={state}
      className={`${baseAnchorClasses} font-mono text-xs text-[hsl(var(--primary))] hover:underline underline-offset-2 ${className}`}
      data-testid={testId || `symbol-link-${symbol}`}
    >
      {symbol}
    </Link>
  );
};

export default CompanyLink;
